/// <amd-module name="Wit/SprintsHub/SprintsView/Components/SprintsView" />
import { RouteParameters } from "Agile/Common/Constants/RoutingConstants";
import { WorkItemStateCategory } from "Agile/Common/Redux/BacklogConfiguration/BacklogConfigurationContracts";
import { IIterationData, ITeam, ITeamSettings } from "Agile/Contracts/Common";
import { TfsComponent } from "Tfs/Platform/TfsComponent";
import { IObservableValue, IReadonlyObservableArray, ObservableArray, ObservableCollection, ObservableValue } from "VSS/Core/Observable";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { format } from "VSS/Core/Util/String";
import { getMenuItems } from "VSS/Features/PlatformUI/ContributedMenu";
import { getTabs } from "VSS/Features/PlatformUI/ContributedTab";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssHistoryService } from "VSS/Platform/History";
import { Components, IVssComponentContext, IVssLayoutManager, WrappedComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { Link } from "VSSUI/Components/Link/Link";
import { Header } from "VSSUI/Header";
import { HeaderCommandBar, IHeaderCommandBarItem, getFilterItem, getFilterItemIconProps } from "VSSUI/HeaderCommandBar";
import { IMenuItem, MenuItemType } from "VSSUI/Menu";
import { Observer, UncheckedObserver } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Splitter, SplitterDirection } from "VSSUI/Splitter";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { IVssContributedTab, Tab, TabBar, TabContent, TabSize } from "VSSUI/Tabs";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { IFilter } from "VSSUI/Utilities/Filter";
import { WorkItemClassificationNode } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { IAutomationRulesService } from "Wit/Common/AutomationRulesService/AutomationRulesService";
import { HubType } from "Wit/Common/BacklogContentService/BacklogContentTreeTypes";
import { IBacklogWorkItemService } from "Wit/Common/BacklogContentService/IBacklogWorkItemService";
import { ICommonBacklogContentService } from "Wit/Common/BacklogContentService/ICommonBacklogContentService";
import { ISprintsHubContentService } from "Wit/Common/BacklogContentService/ISprintsHubContentService";
import { BacklogToolpaneExtension } from "Wit/Common/BacklogToolpaneExtension/BacklogToolpaneExtension";
import { WITCustomerIntelligenceArea, WITCustomerIntelligenceFeature } from "Wit/Common/Constants/CustomerInteligence";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { AgileBacklogPermissionProvider } from "Wit/Common/Controls/Boards/AgileBacklogPermissionContext";
import { ICurrentDirectoryService } from "Wit/Common/CurrentDirectoryService/ICurrentDirectoryService";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { IHubErrorProps } from "Wit/Common/HubError/Components/HubError.props";
import { PlanningPane } from "Wit/Common/PlanningPane/PlanningPane";
import { ISprintSettingsPanelProps } from "Wit/Common/SprintSettingsTypes/Components/SprintSettingsPanel.Props";
import { ITeamSettingsService } from "Wit/Common/TeamServices/TeamSettingsService";
import { ITeamSettingsPanelService } from "Wit/Common/TeamSettingsPanel/Services/TeamSettingsPanelService";
import { IWorkItemFieldService2 } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService2";
import { DirectoryItem } from "Wit/DirectoryService/Client/Contracts/Directory";
import { FilterManager, isFilterStateEmpty } from "Wit/Filter/FilterManager";
import { mapToFilterState } from "Wit/Filter/WorkItemFilter";
import { SprintDates } from "./SprintHeader/SprintDates";
import { IBoardsMessagesService } from "Wit/Messages/BoardsMessagesService";
import { BoardsMessages, ContainerNames } from "Wit/Messages/Components/BoardsMessages";
import {
    GroupByCompatMap,
    RightPaneCompatMap,
    RightPaneViews,
    SettingsConstants,
    SprintsTabs,
    TaskboardGroupBy
} from "Wit/SprintsHub/Common/Constants";
import {
    ColumnToStateMap,
    IColumn,
    ISprintTaskboardData,
    ISprintTaskboardErrorData,
    TaskWorkItemTypesToStateMap
} from "Wit/SprintsHub/Common/Contracts/Interfaces";
import { TaskboardContentView } from "Wit/SprintsHub/SprintsView/Components/Taskboard/TaskboardContentView";
import { WorkDetailsPane } from "Wit/SprintsHub/SprintsView/Components/WorkDetailsPane";
import * as Resources from "Wit/SprintsHub/SprintsView/Resources";
import { ICapacityService } from "Wit/SprintsHub/SprintsView/Services/ICapacityService";
import { ITaskboardService } from "Wit/SprintsHub/SprintsView/Services/ITaskboardService";
import { getWorkItemTypeMenuItems } from "Wit/SprintsHub/SprintsView/Utils/WorkItemTypesDropdownUtils";
import { WorkItemTrackingDataProvider } from "Wit/UI/Data";
import { IWorkItemEventData, IWorkItemEventService, IWorkItemEventType } from "Wit/WorkItemModel/Services/IWorkItemEventService";
import * as React from "react";
import { AnalyticsTab } from "./Analytics/AnalyticsTab";
import { BacklogTab } from "./Backlog/BacklogTab";
import { CapacityTab } from "./Capacity/CapacityTab";
import { SprintHeader } from "./SprintHeader/SprintHeader";
import { SprintsTeachingBubbles } from "./SprintHeader/SprintsTeachingBubbles";
import { SprintPicker } from "./SprintPicker";
import { PeoplePicker } from "./Taskboard/PeoplePicker";
import { showNewTaskboardWorkItemDialog } from "./Taskboard/TaskboardZeroData";
import { MessageCardSeverity } from "VSSUI/MessageCard";
import { BacklogColumnSettings } from "Wit/Common/BacklogContentService/BacklogContentContants";
import { SprintViewOverflowDialog } from "./SprintViewOverflowDialog";

interface ISprintsViewState {
    selectedTeam?: DirectoryItem;
}

interface INormalizedTaskWorkItemTypesToStateMap {
    [workItemTypeNameKey: string]: { [stateNameKey: string]: WorkItemStateCategory };
}

interface INormalizedColumnIdToWorkItemTypeStateMap {
    [columnId: string]: { [workItemTypeNameKey: string]: string };
}

interface ISprintTabContext {
    iterationId: string;
    team: ITeam;
    foregroundInstance: boolean;
}

interface IRightPanelContributionState {
    contributionId: string;
    size: number;
}

/**
 * Settings for a Sprints Hub pivot.
 */
interface ISprintsHubPivotSettings {
    /**
     * Settings for the right panel.
     */
    rightPanelContributionSettings: IRightPanelContributionState;
    /**
     * Group by setting for taskboard pivot.
     */
    groupBy?: string;
}

const DEFAULT_RIGHT_PANE_WIDTH = 420;
const COLUMN_OPTIONS_LINK_TEXT = "Column Options";

const forwardSlashRegex = /\//g;

const tabIdToNameMap = new Map([
    [SprintsTabs.Analytics, Resources.Analytics],
    [SprintsTabs.Capacity, Resources.Capacity],
    [SprintsTabs.SprintBacklog, Resources.Backlog],
    [SprintsTabs.Taskboard, Resources.TaskBoard]
]);

const performanceArea = "Board";
const performanceFeature = "sprints-hub";

export class SprintsView extends TfsComponent<{}, ISprintsViewState> {
    private commonBacklogContentService: ICommonBacklogContentService;
    private currentDirectoryService: ICurrentDirectoryService;
    private teamSettingsPanelService: ITeamSettingsPanelService;
    private workItemEventService: IWorkItemEventService;
    private contributedFilterItemsObservable: IReadonlyObservableArray<IMenuItem>;
    private contributedFilterItems: IMenuItem[] = [];
    private filterToggled: IObservableValue<boolean> = new ObservableValue(false);
    private selectedTabId: ObservableValue<string> = new ObservableValue(SprintsTabs.Taskboard);
    private filter: IFilter;
    private filterManager: FilterManager;
    private iterationId: string;
    private lastAnalyticsRefresh: ObservableValue<Date> = new ObservableValue(new Date());
    private rightHeaderContent: ObservableArray<IHeaderCommandBarItem> = new ObservableArray<IHeaderCommandBarItem>([]);
    private taskboardData: IObservableValue<ISprintTaskboardData | undefined>;
    private taskboardError: IObservableValue<ISprintTaskboardErrorData | undefined>;
    private sprintPicker: IHeaderCommandBarItem;
    private peoplePicker: IHeaderCommandBarItem;
    private timerManagement = new TimerManagement();
    private debouncedSetPaneWidthSetting: (width: number) => void;
    private taskboardService: ITaskboardService;

    private settingsButton: IHeaderCommandBarItem;
    private tabs: IReadonlyObservableArray<IVssContributedTab>;
    private teamSettings: IObservableValue<ITeamSettings | undefined>;
    private bugsBehaviorObservable: IObservableValue<string | undefined>;

    private contributedPaneMenuItems: ObservableArray<IMenuItem> = new ObservableArray([]);
    private selectedIterationPath: ObservableValue<string> = new ObservableValue("");
    private selectedIteration: ObservableValue<IIterationData | undefined> = new ObservableValue(undefined);
    private selectedRightPane = new ObservableValue<string>(RightPaneViews.Off);
    private viewOptionsCheckedMap = new Map<string, ObservableValue<boolean>>();
    private paneWidth: ObservableValue<number> = new ObservableValue(500);
    private pivotContext: ISprintTabContext;
    private showColumnOptionsBubble = new ObservableValue(false);
    private showViewOptionsBubble = new ObservableValue(false);
    private showIterationPickerBubble = new ObservableValue(false);
    private workItemTrackingDataProvider: WorkItemTrackingDataProvider;
    private newTaskboardItemType = new ObservableValue<string>("");
    private groupBy: ObservableValue<TaskboardGroupBy>;
    private hasBacklogManagementPermission: boolean;
    private automationRulesService: IAutomationRulesService;
    private _isLoadingFirstTime = true;
    private backlogWorkItemService: IBacklogWorkItemService;
    private tasksBacklogLevelWorkItemTypes: Set<string>;
    private fieldDataInitialized = new ObservableValue(false);

    private reorderMessageClosed = new ObservableValue(false);

    // Enablement of new Sprints View
    private isNewSprintsViewEnabled = isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.NewSprintsBacklogsView, false);
    private sprintsContentService: ISprintsHubContentService;
    private chartWrapperRef = React.createRef<any>();
    private peoplePickerSelection = new DropdownSelection();
    private peoplePickerItems = new ObservableArray<IMenuItem>();
    private rightHeaderContentButtonCount: ObservableValue<number> = new ObservableValue(5);
    private debouncedButtonCount: () => void;
    private sprintOverflowButton: IHeaderCommandBarItem;
    private headerCommandBarItems: ObservableArray<IHeaderCommandBarItem> = new ObservableArray<IHeaderCommandBarItem>([]);
    private isCardSettingsOptimizationEnabled: boolean;

    public constructor(props: {}, context: IVssComponentContext) {
        super(props, context);

        let fieldsPromise: Promise<IFieldEntry[] | undefined> | undefined = undefined;
        if (isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.CardsUseNewWorkItemFieldService, false)) {
            fieldsPromise = context.pageContext.getService<IWorkItemFieldService2>("IWorkItemFieldService2").loadFields();
        } else {
            this.fieldDataInitialized.value = true;
        }

        fieldsPromise
            ?.then(() => {
                this.fieldDataInitialized.value = true;
            })
            .catch(() => {
                this.fieldDataInitialized.value = true;
            });

        this.workItemEventService = this.context.pageContext.getService<IWorkItemEventService>("IWorkItemEventService");
        const teamSettingsService = context.pageContext.getService<ITeamSettingsService>("ITeamSettingsService");
        this.taskboardService = context.pageContext.getService<ITaskboardService>("ITaskboardService");
        this.teamSettings = teamSettingsService.getTeamSettingsObservable();
        this.bugsBehaviorObservable = new ObservableValue<string | undefined>(this.teamSettings.value?.bugsBehavior?.toString());

        this.isCardSettingsOptimizationEnabled = isFeatureFlagEnabled(context.pageContext, FeatureFlags.ApplySettingsToCardWhenGroupedByAssignedToOptimization, false);

        this.taskboardService.initializeRows();

        this.workItemTrackingDataProvider = new WorkItemTrackingDataProvider(context.pageContext);

        this.setSelectedIterationPath();

        this.getCurrentIteration().then(() => {
            if (!this.taskboardService.selectedIteration && this.selectedIteration && this.selectedIteration.value) {
                this.taskboardService.setIteration(this.selectedIteration.value);
            }
        });

        this.groupBy = this.taskboardService.groupBy;
        this.hasBacklogManagementPermission = this.taskboardService.hasAgileToolsBacklogManagementPermission();
        this.taskboardData = this.taskboardService.getTaskBoardData();
        this.taskboardError = this.taskboardService.getTaskBoardErrorData();
        this.iterationId = this.taskboardData.value?.iterationId ?? "";

        this.teamSettingsPanelService = this.context.pageContext.getService<ITeamSettingsPanelService>("ITeamSettingsPanelService");
        this.currentDirectoryService = this.context.pageContext.getService<ICurrentDirectoryService>("ICurrentDirectoryService");
        const selectedTeam = this.currentDirectoryService.getCurrentDirectory().value;
        this.settingsButton = this.getSettingsButton();

        this.setShowColumnOptionsBubbleValue();
        this.setShowIterationPickerBubbleValue();

        const peoplePickerSelection = new DropdownSelection();
        const peoplePickerItems = new ObservableArray<IMenuItem>();
        this.peoplePicker = PeoplePicker.getPeoplePickerMenuItem(context.pageContext, peoplePickerSelection, peoplePickerItems);
        this.sprintPicker = SprintPicker.getSprintPickerMenuItem(this.onSprintChanged);

        this.setupViewOptions();
        this.debouncedSetPaneWidthSetting = this.timerManagement.debounce(this.setPaneWidthSetting, 500, { trailing: true });

        this.commonBacklogContentService = this.context.pageContext.getService<ICommonBacklogContentService>("ICommonBacklogContentService");
        this.backlogWorkItemService = this.context.pageContext.getService<IBacklogWorkItemService>("IBacklogWorkItemService");

        this.filter = this.commonBacklogContentService.getFilter();
        this.filterManager = this.backlogWorkItemService.getFilterManager() || new FilterManager(this.commonBacklogContentService);

        this.pivotContext = {
            iterationId: this.iterationId,
            team: {
                id: selectedTeam?.id ?? "",
                name: selectedTeam?.name ?? ""
            },
            foregroundInstance: true
        };

        if (this.isNewSprintsViewEnabled) {
            this.commonBacklogContentService = this.context.pageContext.getService<ICommonBacklogContentService>("ICommonBacklogContentService");
            this.sprintsContentService = this.context.pageContext.getService<ISprintsHubContentService>("ISprintsHubContentService");    
            this.sprintOverflowButton = this.getSprintOverflowViewOptionsButton();
            this.debouncedButtonCount = this.timerManagement.debounce(this.updateRightHeaderContentButtonCount, 300, { trailing: true });
        }

        this.tabs = getTabs(this.context.pageContext, "ms.vss-work-web.iteration-backlog-tabs", { contextualData: this.pivotContext });
        this.automationRulesService = this.context.pageContext.getService<IAutomationRulesService>("IAutomationRulesService");
    }

    private getSettingsButton = (): IHeaderCommandBarItem  => {
        const selectedTeam = this.currentDirectoryService.getCurrentDirectory().value;
        const permissions = this.teamSettingsPanelService.getTeamPermissions(selectedTeam?.id || "");
        const isSettingsButtonDisabled = (!this.taskboardData.value && this.selectedTabId.value === SprintsTabs.Taskboard) || !permissions;

        return getSprintSettingsCommandBarButton(
            this.context.pageContext,
            {
                onSave: this.handleSprintSettingsSave,
                selectedTabId: this.selectedTabId,
                taskboardData: this.taskboardData,
                teamId: selectedTeam?.id || ""
            },
            300,
            isSettingsButtonDisabled
        );
    }

    private handleSprintSettingsSave = async (): Promise<boolean> => {
        const taskboardService = this.context.pageContext.getService<ITaskboardService>("ITaskboardService");
        if (this.selectedTabId.value == SprintsTabs.Taskboard) {
            await taskboardService.reloadTaskBoardData();
            if (!this.isCardSettingsOptimizationEnabled) {
                taskboardService.initializeRows(true);
            }
        }

        const teamPanelSettings = this.teamSettingsPanelService.getTeamPanelSettings();
        this.bugsBehaviorObservable.value = teamPanelSettings.value?.bugsBehavior.value;

        return true;
    }

    public componentDidMount() {
        this.filterToggled.value = !isFilterStateEmpty(mapToFilterState(this.filter.getState()));
        this.teamSettings.subscribe(this.setDocumentTitle);
        this.selectedIteration.subscribe(this.setDocumentTitle);
        this.selectedTabId.subscribe(this.setDocumentTitle);
        this.workItemEventService.subscribe(this.onWorkItemSavedCallback.bind(this), IWorkItemEventType.WorkItemSaved);
        this.setDocumentTitle();
        this.bugsBehaviorObservable.subscribe(this.refreshTree);

        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent(WITCustomerIntelligenceArea, WITCustomerIntelligenceFeature.NEW_BOARDS_PLATFORM, {
            source: "Sprints"
        });
        this._isLoadingFirstTime = false;

        if (this.isNewSprintsViewEnabled) {
            window.addEventListener('resize', this.debouncedButtonCount);
            this.updateRightHeaderContentButtonCount();
            this.commonBacklogContentService.getBacklogTree().subscribe(this.getAdditionalContentFFEnabled);
        }
    }

    public componentWillUnmount() {
        this.teamSettings.unsubscribe(this.setDocumentTitle);
        this.selectedIteration.unsubscribe(this.setDocumentTitle);
        this.selectedTabId.unsubscribe(this.setDocumentTitle);
        this.workItemEventService.unsubscribe(this.onWorkItemSavedCallback.bind(this), IWorkItemEventType.WorkItemSaved);
        this.bugsBehaviorObservable.unsubscribe(this.refreshTree);
        this.commonBacklogContentService.getBacklogTree().unsubscribe(this.getAdditionalContentFFEnabled);
        
        if (this.isNewSprintsViewEnabled) {
            window.removeEventListener('resize', this.debouncedButtonCount);
        }

        this.onColumnOptionsBubbleClosed();
        this.onIterationPickerBubbleClosed();
        this.timerManagement.dispose();
    }

    private startSprintsTTIScenario(properties?: { [key: string]: any }) {
        const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        const scenario = performanceService.startScenario(performanceFeature, true);
        if (properties) {
            scenario.addProperties(properties);
        }
    }

    private completeSprintsTTIScenario = () => {
        const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.markTimeToInteractive(performanceArea, performanceFeature, undefined, {
            tab: this.selectedTabId.value
        });
    };

    private updateRightHeaderContentButtonCount = () => {
        this.rightHeaderContentButtonCount.value = window.matchMedia('(max-width: 600px)').matches ? 0 : 5;
    }

    private renderOnTeamSettingsChange = (observerProps: { teamSettings: ITeamSettings | undefined }) => {
        return (
            <>
                {!this.isNewSprintsViewEnabled && (
                    <SprintHeader
                        iterationPath={this.selectedIterationPath}
                        selectedIteration={this.selectedIteration}
                        teamSettings={observerProps.teamSettings!}
                        onSprintDetailsChanged={this.onSprintDetailsChanged}
                    />
                )}
                <Observer
                    selectedIteration={{
                        observableValue: this.selectedIteration,
                        filter: () => {
                            this.getContributedFilterItems();
                            this.setRightHeaderItems();
                            this.isNewSprintsViewEnabled && this.getAdditionalContentFFEnabled();
                            return true;
                        }
                    }}
                    isFiltering={{
                        observableValue: this.filter,
                        filter: () => {
                            this.updateFilterButton();
                        }
                    }}
                    commandBarItems={{
                        observableValue: this.headerCommandBarItems || undefined
                    }}
                    rightHeaderContentButtonCount={{
                        observableValue: this.rightHeaderContentButtonCount
                    }}
                >
                    {(props) => (
                        <>
                            {
                                this.isNewSprintsViewEnabled && (
                                    <SprintHeader
                                        iterationPath={this.selectedIterationPath}
                                        selectedIteration={this.selectedIteration}
                                        teamSettings={observerProps.teamSettings!}
                                        headerCommandItems={props.commandBarItems}
                                        onSprintDetailsChanged={this.onSprintDetailsChanged}
                                    />
                                )
                            }
                            <Header
                                className="sprints-tabbar-header"
                                commandBarClassName="flex-grow justify-end sprints-tabbar-header-commandbar"
                                commandBarItems={this.rightHeaderContent.value}
                                buttonCount={this.isNewSprintsViewEnabled ? props.rightHeaderContentButtonCount : this.rightHeaderContent.value.length}
                                useAriaLabelForOverflow={true}
                            >
                                {this.isNewSprintsViewEnabled ? (
                                    <TabBar
                                        className="full-width flex"
                                        selectedTabId={this.selectedTabId}
                                        tabsClassName="sprints-tabbar-tabs"
                                        onSelectedTabChanged={this.onSelectedTabChanged}
                                        tabSize={TabSize.Compact}
                                    >
                                        <Tab id={SprintsTabs.Taskboard} name={Resources.TaskBoard} />
                                        <Tab id={SprintsTabs.SprintBacklog} name={Resources.Backlog} />
                                        {this.hasBacklogManagementPermission && <Tab id={SprintsTabs.Capacity} name={Resources.Capacity} />}
                                        <Tab id={SprintsTabs.Analytics} name={Resources.Analytics} />
                                    </TabBar>
                                ) : (
                                    <UncheckedObserver
                                        backlogTree={this.commonBacklogContentService.getBacklogTree()}
                                        groupBy={this.groupBy}
                                        taskboardData={this.taskboardData}
                                    >
                                        <TabBar
                                            className="full-width flex"
                                            selectedTabId={this.selectedTabId}
                                            tabsClassName="sprints-tabbar-tabs"
                                            renderAdditionalContent={() => this.getAdditionalContent()}
                                            onSelectedTabChanged={this.onSelectedTabChanged}
                                            tabSize={TabSize.Compact}
                                        >
                                            <Tab id={SprintsTabs.Taskboard} name={Resources.TaskBoard} />
                                            <Tab id={SprintsTabs.SprintBacklog} name={Resources.Backlog} />
                                            {this.hasBacklogManagementPermission && <Tab id={SprintsTabs.Capacity} name={Resources.Capacity} />}
                                            <Tab id={SprintsTabs.Analytics} name={Resources.Analytics} />
                                        </TabBar>
                                    </UncheckedObserver>
                                )}
                            </Header>
                        </>
                    )}
                </Observer>
                { this.isNewSprintsViewEnabled && (
                    <Observer
                        selectedTabId={{
                            observableValue: this.selectedTabId,
                        }}
                    >
                        {(props) => (
                            <div className="sprints-header-dates hide-on-mobile margin-top-8">
                                <div className="flex-row rhythm-horizontal-8 padding-left-8">
                                    <SprintPicker key="sprint-picker-menu-button" onChange={this.onSprintChanged} />
                                    { props.selectedTabId === SprintsTabs.Taskboard && (
                                        <PeoplePicker key="people-picker-menu-button" selection={this.peoplePickerSelection} items={this.peoplePickerItems} />
                                    )}
                                </div>
                                <div className="flex-row rhythm-horizontal-8">
                                    <div key={"sprint-dates"} className="flex flex-column rhythm-horizontal-4">
                                        <SprintDates
                                            selectedIteration={this.selectedIteration}
                                            teamSettings={observerProps.teamSettings!}
                                            onSprintDetailsChanged={this.onSprintDetailsChanged}
                                        />
                                    </div>
                                    <div key="burndown-report-div" onClick={this.showLightBox}>
                                        <Observer
                                            selectedIteration={this.selectedIteration}
                                            iterationPath={this.selectedIterationPath}
                                        >
                                            {(props: {
                                                selectedIteration?: IIterationData;
                                                iterationPath: string;
                                            }) => (
                                                <WrappedComponent
                                                    key="burndown-report"
                                                    wrappedType="BurndownReport"
                                                    wrappedRef={this.chartWrapperRef}
                                                    dependencies={["ms.vss-reporting-web.burndown-report-content"]}
                                                    wrappedProps={{
                                                        teamId: this.teamData?.id,
                                                        teamName: this.teamData?.name,
                                                        projectId: this.projectData?.id,
                                                        iterationPath: props.iterationPath,
                                                        iterationStartDate: props.selectedIteration?.startDate,
                                                        iterationEndDate: props.selectedIteration?.finishDate,
                                                        isMinimalChart: true,
                                                        isLightbox: false,
                                                        lastRefresh: new Date()
                                                    }}
                                                />
                                            )}
                                        </Observer>
                                    </div>
                                </div>
                            </div>
                        )}
                    </Observer>
                )}
                <Observer
                    selectedIteration={this.selectedIteration}
                    selectedTabId={{
                        observableValue: this.selectedTabId,
                        filter: () => {
                            this.updateFilterButton();
                            this.setRightHeaderItems();
                            this.isNewSprintsViewEnabled && this.getAdditionalContentFFEnabled();
                            return true;
                        }
                    }}
                    taskboardData={this.taskboardData}
                    taskboardError={this.taskboardError}
                    isReady={this.fieldDataInitialized}
                >
                    {props => {
                        let pageContent: JSX.Element = <div />;
                        switch (this.selectedTabId.value) {
                            case SprintsTabs.Taskboard:
                                if (!props.taskboardError) {
                                    pageContent = (
                                        <div className={`page-content-top page-content flex-column full-height ${!this.isNewSprintsViewEnabled ? 'scroll-auto' : 'remove-padding-on-mobile'}`}>
                                            <BoardsMessages
                                                className="boards-message-bar flex-noshrink"
                                                containerName={ContainerNames.TaskboardTab}
                                            />
                                            <UncheckedObserver
                                                taskboardFilter={{
                                                    observableValue: this.taskboardService.getFilter(),
                                                    filter: () => {
                                                        this.updateFilterButton();
                                                    }
                                                }}
                                            >
                                                <>
                                                    {props.isReady && (
                                                        <TaskboardContentView
                                                            filterToggled={this.filterToggled}
                                                            groupBy={this.groupBy}
                                                            selectedIteration={props.selectedIteration}
                                                            taskboardData={props.taskboardData!}
                                                            teamSettings={observerProps.teamSettings!}
                                                            newItemType={this.newTaskboardItemType}
                                                            workItemTrackingDataProvider={this.workItemTrackingDataProvider}
                                                            selectedPane={this.selectedRightPane}
                                                            onMarkTTI={this.completeSprintsTTIScenario}
                                                        />
                                                    )}
                                                </>
                                            </UncheckedObserver>
                                        </div>
                                    );
                                } else {
                                    const exceptionInfo = props.taskboardError ? [props.taskboardError.exceptionInfo] : [];
                                    const settingsError = exceptionInfo.find(info => info.isSettingsException);
                                    if (settingsError) {
                                        settingsError.additionalMessagesCustomRender = (message: string) => {
                                            return (
                                                <>
                                                    {message}
                                                    {message === Resources.ConfigurationToFix && (
                                                        <Link onClick={() => this.showTaskboardColumnOptions()}>
                                                            <b>{COLUMN_OPTIONS_LINK_TEXT}</b>.
                                                        </Link>
                                                    )}
                                                </>
                                            );
                                        };
                                    }
                                    pageContent = (
                                        <WrappedComponent<IHubErrorProps>
                                            loadingComponent={() => <Spinner size={SpinnerSize.large} />}
                                            dependencies={["ms.vss-work-web.hub-error"]}
                                            wrappedProps={{
                                                exceptionsInfo: exceptionInfo
                                            }}
                                            wrappedType="ms.vss-work-web.hub-error-component"
                                        />
                                    );
                                }
                                break;
                            case SprintsTabs.SprintBacklog:
                                pageContent = (
                                    <BacklogTab
                                        filter={this.filter}
                                        filterToggled={this.filterToggled}
                                        filterManager={this.filterManager}
                                        taskBoardError={props.taskboardError}
                                        pageContext={this.context.pageContext}
                                        onMarkTTI={this.completeSprintsTTIScenario}
                                        backlogWorkItemService={this.backlogWorkItemService}
                                    />
                                );
                                break;
                            case SprintsTabs.Capacity:
                                pageContent = <CapacityTab selectedIteration={props.selectedIteration} teamSettings={observerProps.teamSettings} />;

                                break;
                            case SprintsTabs.Analytics:
                                pageContent = (
                                    <AnalyticsTab iterationPath={this.selectedIterationPath.value} lastRefresh={this.lastAnalyticsRefresh} />
                                );
                                break;
                            default:
                                // key used to force rerender of tab when iteration changes
                                pageContent = (
                                    <div className="page-content-top page-content flex-grow flex-row h-scroll-hidden">
                                        <TabContent key={"tab:" + this.iterationId} />
                                    </div>
                                );
                        }

                        return (
                            <Observer
                                paneWidth={this.paneWidth}
                                selectedRightPane={this.selectedRightPane}
                                contributedFilterItems={{
                                    observableValue: this.contributedFilterItemsObservable,
                                    filter: this.setContributedFilterItem
                                }}
                            >
                                {(observedProps: { paneWidth: number; selectedRightPane: string }) => {
                                    const showSidePanel =
                                        observedProps.selectedRightPane !== RightPaneViews.Off && this.selectedTabId.value !== SprintsTabs.Analytics;
                                    return (
                                        <Splitter
                                            maxFixedSize={1000}
                                            minFixedSize={300}
                                            className="scroll-hidden"
                                            farElementClassName="flex-column"
                                            nearElementClassName="flex-column"
                                            fixedSize={this.paneWidth.value}
                                            onFixedSizeChanged={width => {
                                                this.paneWidth.value = width;
                                                this.debouncedSetPaneWidthSetting(width);
                                            }}
                                            onRenderNearElement={() => pageContent}
                                            onRenderFarElement={
                                                showSidePanel
                                                    ? () => {
                                                          if (observedProps.selectedRightPane === RightPaneViews.WorkDetails) {
                                                              return (
                                                                  <WorkDetailsPane
                                                                      selectedIteration={props.selectedIteration}
                                                                      teamSettings={observerProps.teamSettings}
                                                                      tasksBacklogLevelWorkItemTypes={this.tasksBacklogLevelWorkItemTypes}
                                                                  />
                                                              );
                                                          } else if (observedProps.selectedRightPane === RightPaneViews.Planning) {
                                                              return (
                                                                  <PlanningPane
                                                                      hub={HubType.Sprints}
                                                                      onDismiss={() => {
                                                                          this.selectRightPane(RightPaneViews.Off, true);
                                                                          this.setShowViewOptionsBubbleValue();
                                                                      }}
                                                                  />
                                                              );
                                                          } else {
                                                              return (
                                                                  <BacklogToolpaneExtension
                                                                      title={
                                                                          this.contributedPaneMenuItems.value.find(
                                                                              item => item.id === observedProps.selectedRightPane
                                                                          )!.data.title
                                                                      }
                                                                      team={this.commonBacklogContentService.getCurrentTeam()}
                                                                      contributionId={observedProps.selectedRightPane}
                                                                      onDismiss={() => {
                                                                          this.selectRightPane(RightPaneViews.Off, true);
                                                                          this.setShowViewOptionsBubbleValue();
                                                                      }}
                                                                  />
                                                              );
                                                          }
                                                      }
                                                    : undefined
                                            }
                                            splitterDirection={SplitterDirection.Vertical}
                                        />
                                    );
                                }}
                            </Observer>
                        );
                    }}
                </Observer>
                <Observer
                    showColumnOptionsBubble={this.showColumnOptionsBubble}
                    showIterationPickerBubble={this.showIterationPickerBubble}
                    showViewOptionsBubble={this.showViewOptionsBubble}
                >
                    {() => (
                        <SprintsTeachingBubbles
                            showColumnOptionsBubble={this.showColumnOptionsBubble}
                            showIterationPickerBubble={this.showIterationPickerBubble}
                            showViewOptionsBubble={this.showViewOptionsBubble}
                        />
                    )}
                </Observer>
            </>
        );
    };

    public render(): JSX.Element {
        return (
            <AgileBacklogPermissionProvider hasBacklogManagementPermission={this.hasBacklogManagementPermission}>
                <Surface background={SurfaceBackground.neutral}>
                    <Page className={`flex-grow flex-column ${this.isNewSprintsViewEnabled && 'sprints-view-ff-enabled sprints-view-container'}`} selectedTabId={this.selectedTabId} tabProviders={this.tabs}>
                        <Observer teamSettings={this.teamSettings} headerCommandItems={this.headerCommandBarItems}>{this.renderOnTeamSettingsChange}</Observer>
                    </Page>
                </Surface>
            </AgileBacklogPermissionProvider>
        );
    }

    private refreshTree = (): void => {
        if (this.selectedTabId.value == SprintsTabs.SprintBacklog) {
            const sprintsContentService = this.context.pageContext.getService<ISprintsHubContentService>("ISprintsHubContentService");
            sprintsContentService.refreshTree({ ignoreBacklogConfigCache: true });
        }
    };

    private showLightBox = () => {
        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => {
            const lightboxProps = {
                title: "",
                onDismiss: onDismiss,
                className: "lightbox-report-content lightbox-sprints-view-ff-enabled"
            };

            const reportSettings = {
                teamId: this.teamData?.id,
                teamName: this.teamData?.name,
                projectId: this.projectData?.id,
                iterationPath: this.selectedIterationPath.value,
                iterationStartDate: this.selectedIteration.value?.startDate,
                iterationEndDate: this.selectedIteration.value?.finishDate,
                isLightbox: true,
                lastRefresh: new Date()
            };

            return (
                <WrappedComponent wrappedType="Lightbox" dependencies={["ms.vss-reporting.reporting-lightbox"]} {...lightboxProps}>
                    <WrappedComponent
                        wrappedType="BurndownReport"
                        dependencies={["ms.vss-reporting-web.burndown-report-content"]}
                        {...reportSettings}
                    />
                </WrappedComponent>
            );
        });
    };

    private getAdditionalContent = (): JSX.Element => {
        let headerItems: IHeaderCommandBarItem[] = [];
        switch (this.selectedTabId.value) {
            case SprintsTabs.Capacity:
                const teamSettings = this.teamSettings.value;
                if (teamSettings && this.selectedIteration.value) {
                    headerItems = this.context.pageContext
                        .getService<ICapacityService>("ICapacityService")
                        .getCapacityCommandBarItems(teamSettings, this.selectedIteration.value.id);
                }
                break;
            case SprintsTabs.SprintBacklog:
                const commonBacklogContentService = this.context.pageContext.getService<ICommonBacklogContentService>("ICommonBacklogContentService");
                const sprintsContentService = this.context.pageContext.getService<ISprintsHubContentService>("ISprintsHubContentService");
                headerItems = commonBacklogContentService.getBacklogTree().value
                    ? commonBacklogContentService.getBacklogCommandBarItems(sprintsContentService)
                    : [];
                break;
            case SprintsTabs.Taskboard:
                const showDropdown = this.taskboardData.value && this.taskboardData.value.newWorkItemTypes.length > 1;
                headerItems = [
                    {
                        id: "taskboard-new-work-item",
                        text: Resources.NewWorkItem,
                        iconProps: { iconName: "Add" },
                        disabled: this.groupBy.value === TaskboardGroupBy.AssignedTo || !this.taskboardData.value,
                        onActivate: showDropdown
                            ? undefined
                            : () => {
                                  if (!this.taskboardService.getAllRows().length) {
                                      showNewTaskboardWorkItemDialog(
                                          this.context.pageContext,
                                          this.taskboardService,
                                          this.selectedIterationPath.value,
                                          this.teamSettings.value,
                                          this.taskboardService.getBacklogConfiguration()?.requirementBacklog
                                      );
                                  } else {
                                      this.newTaskboardItemType.value = this.taskboardData.value!.newWorkItemTypes[0];
                                  }
                              },
                        subtle: true,
                        subMenuProps: showDropdown
                            ? {
                                  id: "taskboard-new-work-item-submenu",
                                  items: getWorkItemTypeMenuItems(
                                      this.taskboardData.value!.newWorkItemTypes,
                                      this.workItemTrackingDataProvider,
                                      this.context.pageContext,
                                      (menuItem: IMenuItem) => {
                                          if (!this.taskboardService.getAllRows().length) {
                                              showNewTaskboardWorkItemDialog(
                                                  this.context.pageContext,
                                                  this.taskboardService,
                                                  this.selectedIterationPath.value,
                                                  this.teamSettings.value,
                                                  this.taskboardService.getBacklogConfiguration()?.requirementBacklog,
                                                  menuItem.id
                                              );
                                          } else {
                                              // show a new card of the specified type
                                              this.newTaskboardItemType.value = menuItem.id;
                                          }
                                      }
                                  )
                              }
                            : undefined
                    },
                    {
                        id: "taskboard-column-options",
                        className: "taskboard-column-options",
                        text: Resources.ColumnOptions,
                        iconProps: { iconName: "Repair" },
                        disabled: !this.taskboardData.value,
                        subtle: true,
                        onActivate: this.showTaskboardColumnOptions
                    }
                ];
                break;
        }

        return <HeaderCommandBar items={headerItems} className="taskboard-header-command-bar flex-grow" />;
    };

    private getAdditionalContentFFEnabled = (): void => {
        switch (this.selectedTabId.value) {
            case SprintsTabs.Capacity:
                const teamSettings = this.teamSettings.value;
                if (teamSettings && this.selectedIteration.value) {
                    this.headerCommandBarItems.value = this.context.pageContext
                        .getService<ICapacityService>("ICapacityService")
                        .getCapacityCommandBarItems(teamSettings, this.selectedIteration.value.id);
                }
                break;
            case SprintsTabs.SprintBacklog:
                const commonBacklogContentService = this.context.pageContext.getService<ICommonBacklogContentService>("ICommonBacklogContentService");
                const sprintsContentService = this.context.pageContext.getService<ISprintsHubContentService>("ISprintsHubContentService");
                this.headerCommandBarItems.value = commonBacklogContentService.getBacklogTree().value
                    ? commonBacklogContentService.getBacklogCommandBarItems(sprintsContentService)
                    : [];
                break;
            case SprintsTabs.Taskboard:
                const showDropdown = this.taskboardData.value && this.taskboardData.value.newWorkItemTypes.length > 1;
                this.headerCommandBarItems.value = [
                    {
                        id: "taskboard-new-work-item",
                        text: Resources.NewWorkItem,
                        iconProps: { iconName: "Add" },
                        disabled: this.groupBy.value === TaskboardGroupBy.AssignedTo || !this.taskboardData.value,
                        onActivate: showDropdown
                            ? undefined
                            : () => {
                                  if (!this.taskboardService.getAllRows().length) {
                                      showNewTaskboardWorkItemDialog(
                                          this.context.pageContext,
                                          this.taskboardService,
                                          this.selectedIterationPath.value,
                                          this.teamSettings.value,
                                          this.taskboardService.getBacklogConfiguration()?.requirementBacklog
                                      );
                                  } else {
                                      this.newTaskboardItemType.value = this.taskboardData.value!.newWorkItemTypes[0];
                                  }
                              },
                        isPrimary: this.isNewSprintsViewEnabled,
                        subtle: !this.isNewSprintsViewEnabled,
                        subMenuProps: showDropdown
                            ? {
                                  id: "taskboard-new-work-item-submenu",
                                  items: getWorkItemTypeMenuItems(
                                      this.taskboardData.value!.newWorkItemTypes,
                                      this.workItemTrackingDataProvider,
                                      this.context.pageContext,
                                      (menuItem: IMenuItem) => {
                                          if (!this.taskboardService.getAllRows().length) {
                                              showNewTaskboardWorkItemDialog(
                                                  this.context.pageContext,
                                                  this.taskboardService,
                                                  this.selectedIterationPath.value,
                                                  this.teamSettings.value,
                                                  this.taskboardService.getBacklogConfiguration()?.requirementBacklog,
                                                  menuItem.id
                                              );
                                          } else {
                                              // show a new card of the specified type
                                              this.newTaskboardItemType.value = menuItem.id;
                                          }
                                      }
                                  )
                              }
                            : undefined
                    },
                    {
                        id: "taskboard-column-options",
                        className: "taskboard-column-options",
                        text: Resources.ColumnOptions,
                        iconProps: { iconName: "Repair" },
                        disabled: !this.taskboardData.value,
                        subtle: !this.isNewSprintsViewEnabled,
                        onActivate: this.showTaskboardColumnOptions
                    }
                ];
                break;
        }
    }

    private getContributedFilterItems = () => {
        const filterItemContext = {
            iteration: this.selectedIteration.value,
            team: {
                id: this.teamData?.id,
                name: this.teamData?.name
            }
        };

        this.contributedFilterItemsObservable = getMenuItems(
            this.context.pageContext,
            "ms.vss-work-web.sprint-board-pivot-filter-menu",
            filterItemContext,
            undefined,
            true
        );

        let contributedItemRank = 100;
        this.contributedFilterItems = this.contributedFilterItemsObservable.value.map(item => {
            return { ...item, rank: contributedItemRank++, ariaLabel: item.text, subtle: true, toolTipProps: { text: item.text }, text: undefined };
        });
    };

    private async setupViewOptions() {
        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");

        this.viewOptionsCheckedMap.set(RightPaneViews.Planning, new ObservableValue(false));
        this.viewOptionsCheckedMap.set(RightPaneViews.WorkDetails, new ObservableValue(false));
        this.viewOptionsCheckedMap.set(RightPaneViews.Off, new ObservableValue(true));
        this.setDefaultGroupByOptions();

        await this.getContributedPaneItems();
        this.tasksBacklogLevelWorkItemTypes = await this.commonBacklogContentService.getWorkItemTypesOfBacklogLevels([BacklogColumnSettings.Tasks]);

        const pivotSettings = settingsService.getEntry<{ [id: string]: ISprintsHubPivotSettings }>(
            SettingsConstants.PivotSettings,
            SettingsUserScope.Me,
            SettingsConstants.Project
        )?.[this.selectedTabId.value];

        if (pivotSettings) {
            this.selectRightPane(pivotSettings.rightPanelContributionSettings.contributionId);
            this.paneWidth.value = pivotSettings.rightPanelContributionSettings.size;
        }

        await this.setGroupByOption(pivotSettings);
    }

    private getContributedPaneItems = async () => {
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const contributionIds = contributionService.getContributionChildren("ms.vss-work-web.iteration-backlog-toolpane");
        if (!contributionIds) {
            return;
        }

        const contributions = (await contributionService.getContributionsAsync(contributionIds)).filter(contribution => contribution);
        if (!contributions || !contributions.length) {
            return;
        }

        contributions.forEach((contribution, index) => this.viewOptionsCheckedMap.set(contributionIds[index], new ObservableValue(false)));
        this.contributedPaneMenuItems.value = contributions.map((item: any, index): IMenuItem => {
            return {
                id: contributionIds[index],
                checked: this.viewOptionsCheckedMap.get(contributionIds[index]),
                text: item.name,
                data: { ...item },
                onActivate: menuItem => {
                    this.selectRightPane(menuItem.id, true);
                }
            };
        });
    };

    private async getCurrentIteration(forceTeamSettingsReload?: boolean) {
        if (!this.teamSettings.value || forceTeamSettingsReload) {
            const teamSettingsService = this.context.pageContext.getService<ITeamSettingsService>("ITeamSettingsService");
            this.teamSettings.value = await teamSettingsService.getTeamSettings({ ignoreCache: true });
        }
        if (this.teamSettings.value?.currentIteration?.friendlyPath === this.selectedIterationPath.value) {
            this.selectedIteration.value = this.teamSettings.value?.currentIteration;
        } else if (this.teamSettings.value) {
            let iterationIndex = this.teamSettings.value.previousIterations.findIndex(
                iteration => iteration.friendlyPath === this.selectedIterationPath.value
            );
            if (iterationIndex !== -1) {
                this.selectedIteration.value = this.teamSettings.value.previousIterations[iterationIndex];
            } else {
                iterationIndex = this.teamSettings.value.futureIterations.findIndex(
                    iteration => iteration.friendlyPath === this.selectedIterationPath.value
                );
                if (iterationIndex !== -1) {
                    this.selectedIteration.value = this.teamSettings.value.futureIterations[iterationIndex];
                }
            }
        }

        this.iterationId = this.selectedIteration.value?.id ?? "";
    }

    private getViewOptionsByTab(selectedTab: string): ObservableCollection<IMenuItem> {
        const viewOptionsSubMenuItems = new ObservableCollection<IMenuItem>();

        const viewOptionsHeader = {
            id: "sprints-view-options-side-pane",
            itemType: MenuItemType.Header,
            text: Resources.SidePane
        };

        const planningMenuItem = {
            checked: this.viewOptionsCheckedMap.get(RightPaneViews.Planning),
            id: "sprints-view-options-planning-pane",
            onActivate: () => {
                this.selectRightPane(RightPaneViews.Planning, true);
            },
            text: Resources.Planning
        };

        const workDetailsMenuItem = {
            checked: this.viewOptionsCheckedMap.get(RightPaneViews.WorkDetails),
            id: "sprints-view-options-work-detail-pane",
            onActivate: () => {
                this.selectRightPane(RightPaneViews.WorkDetails, true);
            },
            text: Resources.WorkDetails
        };

        const viewOptionsSplitterOffItem = {
            checked: this.viewOptionsCheckedMap.get(RightPaneViews.Off),
            id: "sprints-view-options-no-pane",
            onActivate: () => {
                this.selectRightPane(RightPaneViews.Off, true);
            },
            text: Resources.Off
        };

        switch (selectedTab) {
            case SprintsTabs.Taskboard:
                viewOptionsSubMenuItems.push([
                    ...this.getGroupByMenuOptions(),
                    viewOptionsHeader,
                    workDetailsMenuItem,
                    planningMenuItem,
                    viewOptionsSplitterOffItem
                ]);
                break;
            case SprintsTabs.SprintBacklog:
                viewOptionsSubMenuItems.push([viewOptionsHeader, workDetailsMenuItem, planningMenuItem]);
                viewOptionsSubMenuItems.push(this.contributedPaneMenuItems);
                viewOptionsSubMenuItems.push([viewOptionsSplitterOffItem]);
                break;
            case SprintsTabs.Capacity:
                viewOptionsSubMenuItems.push([viewOptionsHeader, workDetailsMenuItem, viewOptionsSplitterOffItem]);
                break;
        }
        return viewOptionsSubMenuItems;
    }

    private normalizeTaskWorkItemTypesToState = (
        normalizedTaskWorkItemTypesToStateMap: INormalizedTaskWorkItemTypesToStateMap,
        witKeyNameMap: { [key: string]: string },
        stateKeyNameMap: { [key: string]: string }
    ) => {
        const taskWorkItemTypesToStateMap: TaskWorkItemTypesToStateMap = this.taskboardData.value?.taskboardModel.taskWorkItemTypesToStateMap || {};

        // normalize taskWorkItemTypesToStateMap
        const taskWorkItemTypes = Object.keys(taskWorkItemTypesToStateMap);
        for (const workItemType of taskWorkItemTypes) {
            const witKey = workItemType.toLocaleLowerCase();
            witKeyNameMap[witKey] = workItemType;
            normalizedTaskWorkItemTypesToStateMap[witKey] = {};

            for (const stateName of Object.keys(taskWorkItemTypesToStateMap[workItemType])) {
                const stateKey = stateName.toLocaleLowerCase();
                stateKeyNameMap[stateKey] = stateName;
                normalizedTaskWorkItemTypesToStateMap[witKey][stateKey] = taskWorkItemTypesToStateMap[workItemType][stateName];
            }
        }
    };

    private normalizeColumnIdToWorkItemTypeState = (
        normalizedColumnIdToWorkItemTypeStateMap: INormalizedColumnIdToWorkItemTypeStateMap,
        normalizedTaskWorkItemTypesToStateMap: INormalizedTaskWorkItemTypesToStateMap,
        columns: IColumn[],
        witKeyNameMap: { [key: string]: string },
        stateKeyNameMap: { [key: string]: string }
    ) => {
        const states: string[] = this.taskboardData.value?.taskboardModel.states || [];
        const columnIdToWorkItemTypeStateMap: ColumnToStateMap = this.taskboardData.value?.taskboardModel.columnIdToWorkItemTypeStateMap || {};

        // If there are no columns then provide the default mapping
        if (columns.length === 0) {
            const taskWorkItemTypes = Object.keys(normalizedTaskWorkItemTypesToStateMap);
            states.forEach((state, index) => {
                const stateKey = state.toLocaleLowerCase();
                // Create a column for the state
                const column = {
                    id: state.toLocaleLowerCase(),
                    name: state,
                    order: index,
                    isNew: true
                };
                normalizedColumnIdToWorkItemTypeStateMap[column.id] = {};
                // Map states for each work item type to the column
                for (const witKey of taskWorkItemTypes) {
                    if (normalizedTaskWorkItemTypesToStateMap[witKey][stateKey]) {
                        normalizedColumnIdToWorkItemTypeStateMap[column.id][witKey] = stateKey;
                    }
                }

                columns.push(column);
            });
        } else {
            // normalize columnIdToWorkItemTypeStateMap
            for (const columnId of Object.keys(columnIdToWorkItemTypeStateMap)) {
                normalizedColumnIdToWorkItemTypeStateMap[columnId] = {};
                for (const workItemTypeName of Object.keys(columnIdToWorkItemTypeStateMap[columnId])) {
                    const witKey = workItemTypeName.toLocaleLowerCase();
                    const state = columnIdToWorkItemTypeStateMap[columnId][workItemTypeName];
                    const stateKey = state.toLocaleLowerCase();

                    witKeyNameMap[witKey] = witKeyNameMap[witKey] || workItemTypeName;
                    stateKeyNameMap[stateKey] = stateKeyNameMap[stateKey] || state;
                    normalizedColumnIdToWorkItemTypeStateMap[columnId][witKey] =
                        normalizedColumnIdToWorkItemTypeStateMap[columnId][witKey] || stateKey;
                }
            }
        }
    };

    private setContributedFilterItem(menuItems: IMenuItem[]) {
        this.contributedFilterItems = menuItems;
    }

    private setSelectedIterationPath() {
        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const state = historyService.getState();
        this.selectedTabId.value = state.pivot ?? SprintsTabs.Taskboard;
        this.selectedIterationPath.value = state["iteration"].replace(forwardSlashRegex, "\\");
    }

    private async setShowColumnOptionsBubbleValue() {
        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
        const taskboardSettings = await settingsService.getEntriesAsync(SettingsConstants.Taskboard, SettingsUserScope.Me);
        this.showColumnOptionsBubble.value = !taskboardSettings[SettingsConstants.ColumnOptionsBubbleSetting];
    }

    private async setShowIterationPickerBubbleValue() {
        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
        const agileSettings = await settingsService.getEntriesAsync(SettingsConstants.SprintsHub, SettingsUserScope.Me);
        this.showIterationPickerBubble.value = !agileSettings[SettingsConstants.IterationPickerBubbleSetting];
    }

    private async setShowViewOptionsBubbleValue() {
        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
        const rightPanel = await settingsService.getEntriesAsync(SettingsConstants.RightPane, SettingsUserScope.Me);
        this.showViewOptionsBubble.value = !rightPanel[SettingsConstants.ViewOptionsBubbleSetting];
    }

    private setTeachingBubbleSetting = (settingsKey: string) => {
        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
        settingsService.setEntries(
            {
                [settingsKey]: true
            },
            SettingsUserScope.Me
        );
    };

    private onColumnOptionsBubbleClosed = () => {
        this.showColumnOptionsBubble.value = false;
        this.setTeachingBubbleSetting(`${SettingsConstants.Taskboard}/${SettingsConstants.ColumnOptionsBubbleSetting}`);
    };

    private onIterationPickerBubbleClosed = () => {
        this.showIterationPickerBubble.value = false;
        this.setTeachingBubbleSetting(`${SettingsConstants.SprintsHub}/${SettingsConstants.IterationPickerBubbleSetting}`);
    };

    private async getReorderMessageClosedValue() {
        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
        const agileSettings = await settingsService.getEntriesAsync(
            SettingsConstants.SprintsHub,
            SettingsUserScope.Me,
            SettingsConstants.Project,
            this.projectData?.id
        );
        this.reorderMessageClosed.value = agileSettings[SettingsConstants.ReorderMessageClosed];
    }

    private showTaskboardColumnOptions = () => {
        const columns = this.taskboardData.value ? [...this.taskboardData.value.taskboardModel.columns] : [];
        const witKeyNameMap: { [key: string]: string } = {};
        const stateKeyNameMap: { [key: string]: string } = {};
        const normalizedTaskWorkItemTypesToStateMap: INormalizedTaskWorkItemTypesToStateMap = {};
        const normalizedColumnIdToWorkItemTypeStateMap: INormalizedColumnIdToWorkItemTypeStateMap = {};

        this.normalizeTaskWorkItemTypesToState(normalizedTaskWorkItemTypesToStateMap, witKeyNameMap, stateKeyNameMap);
        this.normalizeColumnIdToWorkItemTypeState(
            normalizedColumnIdToWorkItemTypeStateMap,
            normalizedTaskWorkItemTypesToStateMap,
            columns,
            witKeyNameMap,
            stateKeyNameMap
        );

        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => (
            <Observer teamSettings={this.teamSettings}>
                {(props: { teamSettings: ITeamSettings | undefined }) => (
                    <WrappedComponent
                        wrappedType={"taskboard-custom-column-panel"}
                        dependencies={["ms.vss-work-web.agile-taskboard-custom-column-panel"]}
                        columns={columns}
                        normalizedTaskWorkItemTypesToStateMap={normalizedTaskWorkItemTypesToStateMap}
                        normalizedColumnIdToWorkItemTypeStateMap={normalizedColumnIdToWorkItemTypeStateMap}
                        witKeyNameMap={witKeyNameMap}
                        stateKeyNameMap={stateKeyNameMap}
                        teamId={props.teamSettings?.teamId}
                        onClose={onDismiss}
                    />
                )}
            </Observer>
        ));
    };

    private onSelectedTabChanged = async (tabId: string) => {
        if (!this._isLoadingFirstTime) {
            this.startSprintsTTIScenario({
                inPageNavigation: true,
                switchTab: true,
                tab: tabId
            });
        }

        const previousTab = this.selectedTabId.value;
        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const state = historyService.getState();

        // Clear the filter state on tab selection
        const routeValues: { [key: string]: string } = {
            [RouteParameters.Iteration]: state.iteration,
            [RouteParameters.Pivot]: tabId,
            [RouteParameters.Project]: state.project,
            [RouteParameters.TeamName]: state.teamName,
            [RouteParameters.ViewName]: state.viewname
        };
        historyService.replaceState(routeValues);
        this.selectedTabId.value = tabId;

        if (tabId === SprintsTabs.Taskboard && previousTab !== SprintsTabs.Taskboard) {
            // The contents of the taskboard could have changed while we were on a different tab
            // Refresh tree and try to reload all taskboard
            this.commonBacklogContentService.refreshTree();
            await this.taskboardService.reloadTaskBoardData();
        }

        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");

        const pivotSettings = settingsService.getEntry<{ [id: string]: ISprintsHubPivotSettings }>(
            SettingsConstants.PivotSettings,
            SettingsUserScope.Me,
            SettingsConstants.Project
        )?.[this.selectedTabId.value];

        if (pivotSettings) {
            this.selectRightPane(pivotSettings.rightPanelContributionSettings.contributionId);
            this.paneWidth.value = pivotSettings.rightPanelContributionSettings.size;
        } else {
            this.selectRightPane(RightPaneViews.Off);
        }

        await this.setGroupByOption(pivotSettings);

        const entries = {
            [SettingsConstants.ContentPivotSetting]: tabId
        };

        settingsService.setEntries(entries, SettingsUserScope.Me, SettingsConstants.Project, this.projectData?.id);

        this.onColumnOptionsBubbleClosed();
        this.onIterationPickerBubbleClosed();

        const messagesService = this.context.pageContext.getService<IBoardsMessagesService>("IBoardsMessagesService");
        messagesService.dismissMessage(true);

        this.settingsButton = this.getSettingsButton();
        this.setRightHeaderItems();
        this.isNewSprintsViewEnabled && this.getAdditionalContentFFEnabled();
    };

    private onSprintChanged = async (iteration?: IIterationData, iterationIsChanged?: boolean) => {
        if (!this._isLoadingFirstTime) {
            this.startSprintsTTIScenario({
                inPageNavigation: true,
                switchSprint: true,
                tab: this.selectedTabId.value
            });
        }
        if (iteration) {
            this.selectedIterationPath.value = iteration.friendlyPath;
            this.selectedIteration.value = iteration;
            this.iterationId = iteration.id;
            this.pivotContext.iterationId = this.iterationId;
            await this.taskboardService.reloadTaskBoardData();
            this.taskboardService.initializeRows();
            this.taskboardService.setIteration(iteration);
        }

        if (iterationIsChanged) {
            const sprintsContentService = this.context.pageContext.getService<ISprintsHubContentService>("ISprintsHubContentService");
            sprintsContentService.refreshTree({ filterManager: this.filterManager });
        }
    };

    private onSprintDetailsChanged = async (node: WorkItemClassificationNode) => {
        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const newPath = node.path.split("\\").filter(x => x);
        newPath.splice(1, 1);
        const routeValues: { [key: string]: string } = {
            [RouteParameters.Project]: historyService.getState().project,
            [RouteParameters.TeamName]: this.teamSettings.value!.teamName!,
            [RouteParameters.Iteration]: newPath.join("/"),
            [RouteParameters.Pivot]: historyService.getState().pivot
        };
        historyService.pushState(routeValues);
        this.setSelectedIterationPath();
        this.getCurrentIteration(true);
    };

    private setRightHeaderItems = () => {
        const sprintsViewOptions: IHeaderCommandBarItem = {
            ariaLabel: Resources.ViewOptions,
            className: "sprints-view-options",
            hideDropdownIcon: true,
            iconProps: { iconName: "Equalizer" },
            id: "sprints-view-options",
            subMenuProps: {
                id: "sprints-view-options-menu",
                items: this.getViewOptionsByTab(this.selectedTabId.value)
            },
            rank: 3,
            subtle: true,
            tooltipProps: { text: Resources.ViewOptions }
        };

        if (this.selectedTabId.value === SprintsTabs.SprintBacklog) {
            if (this.isNewSprintsViewEnabled) {
                this.rightHeaderContent.value = [
                    sprintsViewOptions,
                    this.sprintOverflowButton,
                    this.toggleFilterButton,
                    this.settingsButton,
                    this.fullScreenButton
                ];
            } else {
                this.rightHeaderContent.value = [
                    this.sprintPicker,
                    sprintsViewOptions,
                    this.toggleFilterButton,
                    this.settingsButton,
                    this.fullScreenButton
                ];
            }
        } else if (this.selectedTabId.value === SprintsTabs.Taskboard) {
            if (this.isNewSprintsViewEnabled) {
                this.rightHeaderContent.value = [
                    sprintsViewOptions,
                    this.sprintOverflowButton,
                    this.toggleFilterButton,
                    ...this.contributedFilterItems,
                    this.settingsButton,
                    this.fullScreenButton
                ];
            } else {
                this.rightHeaderContent.value = [
                    this.sprintPicker,
                    this.peoplePicker,
                    sprintsViewOptions,
                    this.toggleFilterButton,
                    ...this.contributedFilterItems,
                    this.settingsButton,
                    this.fullScreenButton
                ];
            }
        } else if (this.selectedTabId.value === SprintsTabs.Capacity) {
            if (this.isNewSprintsViewEnabled) {
                this.rightHeaderContent.value = [sprintsViewOptions, this.sprintOverflowButton, this.settingsButton, this.fullScreenButton];
            } else {
                this.rightHeaderContent.value = [this.sprintPicker, sprintsViewOptions, this.settingsButton, this.fullScreenButton];
            }
        } else if (this.selectedTabId.value === SprintsTabs.Analytics) {
            if (this.isNewSprintsViewEnabled) {
                this.rightHeaderContent.value = [
                    ...this.contributedFilterItems,
                    AnalyticsTab.getRefreshMenuItem(this.lastAnalyticsRefresh),
                    this.fullScreenButton
                ];
            } else {
                this.rightHeaderContent.value = [
                    this.sprintPicker,
                    ...this.contributedFilterItems,
                    AnalyticsTab.getRefreshMenuItem(this.lastAnalyticsRefresh),
                    this.fullScreenButton
                ];
            }
        } else {
            if (this.isNewSprintsViewEnabled) {
                this.rightHeaderContent.value = [this.fullScreenButton];
            } else {
                this.rightHeaderContent.value = [this.sprintPicker, this.fullScreenButton];
            }
        }
    };

    private getSprintOverflowViewOptionsButton = (): IHeaderCommandBarItem => {
        return this.manageSprintOverflowViewOptionsButton(this.context.pageContext);
    }

    private manageSprintOverflowViewOptionsButton = (pageContext: IVssPageContext) => {
        const { 
            teamData, 
            projectData, 
            chartWrapperRef, 
            selectedIteration, 
            selectedIterationPath, 
            peoplePickerItems, 
            peoplePickerSelection, 
            teamSettings, 
        } = this;
        const chartProps = { teamData, projectData, chartWrapperRef };
        const iterationProps = { selectedIteration, selectedIterationPath };
        const peoplePickerProps = { peoplePickerItems, peoplePickerSelection };
        const sprintsOverflowDialogButton: IHeaderCommandBarItem = {
            ariaLabel: Resources.ViewOverflowSprintOptions,
            className: "sprints-view-overflow-options show-on-mobile",
            iconProps: { iconName: "Sprint" },
            id: "sprints-view-overflow-options",
            onActivate: () => {
                pageContext.getService<IVssLayoutManager>("IVssLayoutManager").renderCallout(onDismiss => (
                    <Observer teamSettings={teamSettings}>
                        {(props: {
                            teamSettings?: ITeamSettings | undefined;
                        }) => (
                            <SprintViewOverflowDialog 
                                chartProps={chartProps}
                                iterationProps={iterationProps}
                                onShowLightBox={this.showLightBox}
                                onSprintChanged={this.onSprintChanged}
                                onSprintDetailsChanged={this.onSprintDetailsChanged}
                                peoplePickerProps={peoplePickerProps}
                                onPeoplePickerChanged={this.onPeoplePickerChanged}
                                teamSettings={props.teamSettings!}
                                onDismiss={onDismiss}
                            />
                        )}
                    </Observer>

                ));
            },
            rank: 3,
            subtle: true,
            tooltipProps: { text: Resources.ViewOverflowSprintOptions }
        }

        return sprintsOverflowDialogButton;
    }

    private onPeoplePickerChanged = (peoplePickerSelection: DropdownSelection, peoplePickerItems: ObservableArray<IMenuItem>): void => {
        this.peoplePickerItems = peoplePickerItems;
        this.peoplePickerSelection = peoplePickerSelection;
    }

    private getGroupByMenuOptions() {
        return [
            {
                id: "sprints-view-options-group-by",
                itemType: MenuItemType.Header,
                text: Resources.GroupBy
            },
            {
                checked: this.viewOptionsCheckedMap.get(TaskboardGroupBy.AssignedTo),
                id: "sprints-view-options-group-by-people",
                onActivate: () => {
                    this.selectGroupByOption(TaskboardGroupBy.AssignedTo);
                },
                text: Resources.AssignedTo
            },
            {
                checked: this.viewOptionsCheckedMap.get(TaskboardGroupBy.Parents),
                id: "sprints-view-options-group-by-parents",
                onActivate: () => {
                    this.selectGroupByOption(TaskboardGroupBy.Parents);
                },
                text: Resources.Parents
            }
        ];
    }

    private selectRightPane(selectedPane: string, udpateSettings?: boolean) {
        // Turn off current checked pane option
        if (this.viewOptionsCheckedMap.has(this.selectedRightPane.value)) {
            this.viewOptionsCheckedMap.get(this.selectedRightPane.value)!.value = false;
        }

        const mappedSelectedPane = RightPaneCompatMap[selectedPane] ?? selectedPane;

        this.selectedRightPane.value = mappedSelectedPane;

        if (this.viewOptionsCheckedMap.has(mappedSelectedPane)) {
            this.viewOptionsCheckedMap.get(mappedSelectedPane)!.value = true;
        }

        if (udpateSettings) {
            const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");

            let settings = settingsService.getEntry<{ [id: string]: ISprintsHubPivotSettings }>(
                SettingsConstants.PivotSettings,
                SettingsUserScope.Me,
                SettingsConstants.Project
            );

            settings = this.setupSettingsObject(settings);
            settings[this.selectedTabId.value].rightPanelContributionSettings.contributionId = mappedSelectedPane;

            settingsService.setEntries(
                { [SettingsConstants.PivotSettings]: settings },
                SettingsUserScope.Me,
                SettingsConstants.Project,
                this.projectData?.id
            );
        }
    }

    private async selectGroupByOption(selectedOption: TaskboardGroupBy) {
        // Turn off current checked group by option

        if (this.viewOptionsCheckedMap.has(this.groupBy.value)) {
            this.viewOptionsCheckedMap.get(this.groupBy.value)!.value = false;
        }

        this.groupBy.value = selectedOption;

        if (this.viewOptionsCheckedMap.has(selectedOption)) {
            this.viewOptionsCheckedMap.get(selectedOption)!.value = true;
        }

        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");

        let settings = settingsService.getEntry<{ [id: string]: ISprintsHubPivotSettings }>(
            SettingsConstants.PivotSettings,
            SettingsUserScope.Me,
            SettingsConstants.Project
        );

        settings = this.setupSettingsObject(settings);
        settings[this.selectedTabId.value].groupBy = selectedOption;

        settingsService.setEntries(
            { [SettingsConstants.PivotSettings]: settings },
            SettingsUserScope.Me,
            SettingsConstants.Project,
            this.projectData?.id
        );

        if (this.selectedTabId.value === SprintsTabs.Taskboard) {
            if (this.groupBy.value === TaskboardGroupBy.AssignedTo && this.taskboardService.getFilteredRows().length) {
                this.setReorderMessageClosedValue(settingsService, false);
                this.showReorderMessage(settingsService);
            } else {
                await this.getReorderMessageClosedValue();
                if (!this.reorderMessageClosed.value) {
                    const messagesService = this.context.pageContext.getService<IBoardsMessagesService>("IBoardsMessagesService");
                    messagesService.dismissMessage(true);
                }
            }
        }
    }

    private setDefaultGroupByOptions = () => {
        this.viewOptionsCheckedMap.set(TaskboardGroupBy.Parents, new ObservableValue(false));
        this.viewOptionsCheckedMap.set(TaskboardGroupBy.AssignedTo, new ObservableValue(false));
    };

    private setGroupByOption = async (pivotSettings: ISprintsHubPivotSettings | undefined) => {
        this.setDefaultGroupByOptions();

        if (pivotSettings?.groupBy) {
            this.groupBy.value = GroupByCompatMap[pivotSettings.groupBy] ?? pivotSettings.groupBy;
        } else {
            this.groupBy.value = TaskboardGroupBy.Parents;
        }

        if (this.viewOptionsCheckedMap.has(this.groupBy.value)) {
            this.viewOptionsCheckedMap.get(this.groupBy.value)!.value = true;
        }

        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
        await this.getReorderMessageClosedValue();

        if (
            this.groupBy.value === TaskboardGroupBy.AssignedTo &&
            this.selectedTabId.value === SprintsTabs.Taskboard &&
            this.taskboardService.getFilteredRows().length &&
            !this.reorderMessageClosed.value
        ) {
            this.showReorderMessage(settingsService);
        }
    };

    private showReorderMessage = (settingsService: ISettingsService) => {
        const messagesService = this.context.pageContext.getService<IBoardsMessagesService>("IBoardsMessagesService");
        messagesService.showMessage({
            renderMessage: () => Resources.PeopleClassificationReorderMessage,
            severity: MessageCardSeverity.Warning,
            onDismiss: isClosedProgrammatically => {
                if (!isClosedProgrammatically) {
                    this.setReorderMessageClosedValue(settingsService, true);
                }
            },
            containerName: ContainerNames.TaskboardTab
        });
    };

    private setReorderMessageClosedValue = (settingsService: ISettingsService, reorderMessageClosedValue: boolean) => {
        settingsService.setEntries(
            {
                [`${SettingsConstants.SprintsHub}/${SettingsConstants.ReorderMessageClosed}`]: reorderMessageClosedValue
            },
            SettingsUserScope.Me,
            SettingsConstants.Project,
            this.projectData?.id
        );
    };

    private setDocumentTitle = () => {
        const teamName = this.teamSettings.value?.teamName;
        const currentIteration = this.selectedIteration.value?.name;
        const currentTab = tabIdToNameMap.get(this.selectedTabId.value) ?? "";
        if (teamName && currentIteration) {
            document.title = format(Resources.SprintsDocumentTitle, teamName, currentIteration, currentTab);
        }
    };

    private setPaneWidthSetting = (width: number) => {
        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");

        let settings = settingsService.getEntry<{ [id: string]: ISprintsHubPivotSettings }>(
            SettingsConstants.PivotSettings,
            SettingsUserScope.Me,
            SettingsConstants.Project
        );

        settings = this.setupSettingsObject(settings);
        settings[this.selectedTabId.value].rightPanelContributionSettings.size = width;

        settingsService.setEntries(
            { [SettingsConstants.PivotSettings]: settings },
            SettingsUserScope.Me,
            SettingsConstants.Project,
            this.projectData?.id
        );
    };

    private setupSettingsObject(initialSettings: { [id: string]: ISprintsHubPivotSettings } | undefined): { [id: string]: ISprintsHubPivotSettings } {
        let settings = initialSettings;

        if (!settings) {
            settings = {
                [this.selectedTabId.value]: {
                    rightPanelContributionSettings: { contributionId: this.selectedRightPane.value, size: DEFAULT_RIGHT_PANE_WIDTH }
                }
            };
        } else {
            if (!settings[this.selectedTabId.value]) {
                settings[this.selectedTabId.value] = {
                    rightPanelContributionSettings: { contributionId: this.selectedRightPane.value, size: DEFAULT_RIGHT_PANE_WIDTH }
                };
            }
        }

        return settings;
    }

    private fullScreenButton: IHeaderCommandBarItem = {
        ariaLabel: Resources.FullScreenEnter,
        iconProps: { iconName: "FullScreen" },
        id: "full-screen",
        onActivate: () => {
            const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
            layoutManager.toggleFullScreenMode();
            if (layoutManager.getFullScreenMode().value) {
                this.fullScreenButton.iconProps = { iconName: "BackToWindow" };
                this.fullScreenButton.ariaLabel = Resources.FullScreenExit;
            } else {
                this.fullScreenButton.iconProps = { iconName: "FullScreen" };
                this.fullScreenButton.ariaLabel = Resources.FullScreenEnter;
            }
            this.rightHeaderContent.change(this.rightHeaderContent.value.indexOf(this.fullScreenButton), this.fullScreenButton);
        },
        subtle: true
    };

    private toggleFilterButton: IHeaderCommandBarItem = {
        ...getFilterItem(
            () => {
                this.filterToggled.value = !this.filterToggled.value;
            },
            /* isFiltering */ false,
            /* important */ this.isNewSprintsViewEnabled ? undefined : true,
            Resources.Filter
        ),
        rank: 5
    };

    private updateFilterButton(): void {
        let isFiltering = false;
        if (this.selectedTabId.value === SprintsTabs.SprintBacklog) {
            isFiltering = this.filterManager.isFiltering();
        } else if (this.selectedTabId.value === SprintsTabs.Taskboard) {
            isFiltering = this.taskboardService.getFilterManager().isFiltering();
        }
        this.toggleFilterButton.iconProps = getFilterItemIconProps(isFiltering);

        const filterButtonIdx = this.rightHeaderContent.value.indexOf(this.toggleFilterButton);
        if (filterButtonIdx >= 0) {
            this.rightHeaderContent.change(filterButtonIdx, this.toggleFilterButton);
        }
    }

    private async onWorkItemSavedCallback(workItemEventData: IWorkItemEventData): Promise<void> {
        if (isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.TeamAutomationRulesEnabled, false)) {
            await this.automationRulesService.evaluateAutomationRules(workItemEventData.workItem);
        }
        this.taskboardService.workItemChangedCallback(workItemEventData);
    }
}

function getSprintSettingsCommandBarButton(
    pageContext: IVssPageContext,
    panelProps: Omit<ISprintSettingsPanelProps, "onDismiss">,
    rank: number,
    settingsButtonDisabled: boolean
): IHeaderCommandBarItem {
    const settingsButton: IHeaderCommandBarItem = {
        ariaLabel: Resources.SettingsButtonTooltip,
        disabled: settingsButtonDisabled,
        iconProps: { iconName: "Settings" },
        id: "team-settings",
        onActivate: () => {
            pageContext.getService<IVssLayoutManager>("IVssLayoutManager").renderCallout(onDismiss => (
                <WrappedComponent<ISprintSettingsPanelProps>
                    dependencies={["ms.vss-work-web.sprint-settings-panel"]}
                    wrappedType={"ms.vss-work-web.sprint-settings-panel-component"}
                    wrappedProps={{
                        ...panelProps,
                        onDismiss
                    }}
                />
            ));
        },
        rank,
        subtle: true,
        tooltipProps: { text: Resources.SettingsButtonTooltip }
    };

    return settingsButton;
}

Components.add("sprints-view-component", SprintsView);
